import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './CreateNewPassword.css';
import logo from '../assets/xtrafyls.svg';
import { signin_profile, success_tick, passwordIcon } from '../assets';
import { resetPassword, clearError } from '../features/auth/authSlice';

const CreateNewPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const { isLoading, error, verificationEmail } = useSelector((state) => state.auth);
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState({});

  // Use ref instead of state - ref updates synchronously to prevent race condition
  const isPasswordResetSuccessRef = useRef(false);

  // Redirect if no verification email (but not if password was just reset successfully)
  useEffect(() => {
    if (!verificationEmail && !isPasswordResetSuccessRef.current) {
      navigate('/forgot-password');
    }
  }, [verificationEmail, navigate]);

  // Show error if exists
  useEffect(() => {
    if (error) {
      setErrors({ general: error });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});

    // Validate passwords
    if (!newPassword || !confirmPassword) {
      setErrors({ general: 'Please fill in all fields' });
      return;
    }

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setErrors({ password: passwordErrors[0] });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setErrors({ confirm: 'Passwords do not match!' });
      return;
    }
    
    try {
      // Set flag BEFORE dispatch to prevent redirect race condition
      // Redux updates verificationEmail to null when fulfilled, which would trigger redirect
      isPasswordResetSuccessRef.current = true;

      const resultAction = await dispatch(
        resetPassword({
          email: verificationEmail,
          password: newPassword
        })
      );

      if (resetPassword.fulfilled.match(resultAction)) {
        // Show success modal
        setShowSuccessModal(true);
        document.body.style.overflow = 'hidden';

        // Redirect to signin after 2 seconds
        setTimeout(() => {
          setShowSuccessModal(false);
          document.body.style.overflow = 'unset';
          navigate('/signin');
        }, 2000);
      } else {
        // Reset the flag on failure so redirect can work if needed
        isPasswordResetSuccessRef.current = false;

        const errorMessage = resultAction.payload || 'Failed to reset password. Please try again.';
        setErrors({ general: errorMessage });
      }
    } catch (error) {
      // Reset the flag on error
      isPasswordResetSuccessRef.current = false;
      setErrors({ general: 'Failed to reset password. Please try again.' });
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    document.body.style.overflow = 'unset';
    navigate('/signin');
  };

  return (
    <div className="create-password-container">
      {/* Left Side - Purple Hero Section */}
      <div className="create-password-left">
        <div className="left-content">
          {/* Logo */}
          <img src={logo} alt="Xtrafyl" className="logo" />

          {/* Hero Text */}
          <div className="hero-section">
            <h1 className="hero-title">
              Fuel Your Business<br />
              With <span className="highlight">Xtrafyl.</span>
            </h1>
            <p className="hero-description text-big-body">
              From fuel delivery to charging, car wash, and more — we help you reach
              more customers, every day.
            </p>
          </div>
        </div>

      </div>

      {/* Right Side - Create Password Form */}
      <div className="create-password-right">
        <div className="create-password-right-content">
          {/* Header */}
          <div className="form-header">
            <h2>Create New Password</h2>
            <p className="text-small-body">
              Create a strong and secure password to keep your account safe.
            </p>
          </div>

          <div className="form-container">
            {/* Form */}
            <form onSubmit={handleSubmit} className="create-password-form">
              {/* General Error Message */}
              {errors.general && (
                <div className="error-banner">
                  {errors.general}
                </div>
              )}

              {/* New Password Input */}
              <div className="form-group">
                <label htmlFor="newPassword" className="text-label">New Password</label>
                <div className={`input-wrapper ${errors.password ? 'input-error' : ''}`}>
                  <img src={passwordIcon} alt="" className="input-icon" />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="text-small-body"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label="Toggle password visibility"
                  >
                    {showNewPassword ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 10C2.5 10 5 4.16667 10 4.16667C15 4.16667 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.95 14.95C13.5255 16.0358 11.7904 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35558 9.06831 4.52744 7.48604 5.95 6.16667M8.25 4.53333C8.82361 4.39907 9.41083 4.33195 10 4.33333C15 4.33333 17.5 10.1667 17.5 10.1667C17.0125 11.0756 16.4208 11.9272 15.7367 12.7033M11.7667 11.7667C11.5379 12.0123 11.2617 12.2093 10.9545 12.3459C10.6474 12.4826 10.316 12.556 9.97979 12.562C9.64359 12.568 9.30979 12.5063 8.99812 12.3806C8.68645 12.2549 8.40348 12.0675 8.16627 11.8303C7.92906 11.5931 7.7417 11.3101 7.61598 10.9985C7.49027 10.6868 7.42859 10.353 7.43458 10.0168C7.44057 9.68062 7.51409 9.34921 7.65074 9.04206C7.78738 8.73491 7.98442 8.45868 8.23 8.23" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2.5 2.5L17.5 17.5" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <span className="field-error">{errors.password}</span>
                )}
              </div>

              {/* Confirm Password Input */}
              <div className="form-group confirm_pass">
                <label htmlFor="confirmPassword" className="text-label">Confirm Password</label>
                <div className={`input-wrapper ${errors.confirm ? 'input-error' : ''}`}>
                  <img src={passwordIcon} alt="" className="input-icon" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="text-small-body"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label="Toggle password visibility"
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 10C2.5 10 5 4.16667 10 4.16667C15 4.16667 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.95 14.95C13.5255 16.0358 11.7904 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35558 9.06831 4.52744 7.48604 5.95 6.16667M8.25 4.53333C8.82361 4.39907 9.41083 4.33195 10 4.33333C15 4.33333 17.5 10.1667 17.5 10.1667C17.0125 11.0756 16.4208 11.9272 15.7367 12.7033M11.7667 11.7667C11.5379 12.0123 11.2617 12.2093 10.9545 12.3459C10.6474 12.4826 10.316 12.556 9.97979 12.562C9.64359 12.568 9.30979 12.5063 8.99812 12.3806C8.68645 12.2549 8.40348 12.0675 8.16627 11.8303C7.92906 11.5931 7.7417 11.3101 7.61598 10.9985C7.49027 10.6868 7.42859 10.353 7.43458 10.0168C7.44057 9.68062 7.51409 9.34921 7.65074 9.04206C7.78738 8.73491 7.98442 8.45868 8.23 8.23" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2.5 2.5L17.5 17.5" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirm && (
                  <span className="field-error">{errors.confirm}</span>
                )}
              </div>

              {/* Update Password Button */}
              <button
                type="submit"
                className="update-password-button text-button"
                disabled={isLoading}
              >
                {isLoading ? 'UPDATING...' : 'UPDATE PASSWORD'}
              </button>

              {/* Sign In Link */}
              <p className="signin-link text-small-body">
                Remember your password? <span onClick={() => navigate('/signin')}>Sign In</span>
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="password-success-modal-overlay" onClick={handleCloseModal}>
          <div className="password-success-modal-content" onClick={(e) => e.stopPropagation()}>
            
            {/* Close Button */}
            <button className="modal-close-button" onClick={handleCloseModal}>
              ✕
            </button>

            {/* Success Icon */}
            <div className="success-icon-container">
              <img src={success_tick} alt="Success" style={{ width: '120px', height: '120px' }} />
            </div>

            {/* Success Message */}
            <h3 className="success-message-title">Password Updated Successfully!</h3>
            <p className="success-message-subtitle">Redirecting to sign in...</p>

          </div>
        </div>
      )}
    </div>
  );
};

export default CreateNewPassword;